// oxlint-disable unicorn/prefer-add-event-listener -- IndexedDB request/transaction handlers are single-owner promises.
/**
 * IndexedDB wrapper for the persistent (L2) message cache.
 *
 * Ported from v1 (`mobile/src/lib/cache/idb.ts`) but rewritten on the *raw*
 * IndexedDB API instead of the `idb` npm package, so the cache adds ZERO new
 * dependencies to packages/ui — it is fully self-contained under `lib/cache/`.
 *
 * Schema is intentionally generic: one `messages` store keyed by message id,
 * with composite indexes that let us:
 *   - read the N most recent messages for a scope (channel or DM) in
 *     `[scopeKey, createdAt]` order;
 *   - resolve a thread's replies via `parentId`.
 *
 * Scope keys are constructed in `scope.ts` (`wsId:ch:channelId`,
 * `wsId:dm:peerId`) so both message surfaces share a single store and a
 * single eviction policy. (v1 also had a `dma` agent-DM scope — the rewrite
 * routes agent conversations through `agentStreamState`, not selectDm, so
 * there is no agent-DM scope here.)
 *
 * The `syncMeta` store holds per-scope bookkeeping (last sync cursor, last
 * read cursor, last access time for LRU eviction). `appMeta` stores singleton
 * flags (currently the user id we cached for, so a different user opening the
 * same WebView triggers a wipe).
 *
 * SSR-safe + WebView-safe: every helper short-circuits when `indexedDB` is
 * unavailable (server, very old browser, Safari private mode for the first
 * session) and every operation is wrapped so a failure degrades to "no cache"
 * rather than throwing into the caller.
 */

export const DB_NAME = "cyborg7-cache";
// v2 (#502): adds the `outbox` store for messages whose send couldn't be
// delivered (socket down / unauthenticated). Persisting it means an offline send
// survives a reload instead of being lost with the in-memory replay queue.
//
// v3 (CYBORG-897 phase 4): the `messages` store persists `attachments` VERBATIM,
// which before this phase meant expiring signed CloudFront urls. A cold paint
// replays them, so every cached row would render from a url that is already dead
// (or, once the bucket is closed, forbidden) until the network answer arrives.
// The whole store is dropped ONCE on upgrade rather than migrated row by row:
// this is a CACHE, every row is re-fetchable, and one `deleteObjectStore` is
// both cheaper and less to get wrong than rewriting attachments inside an
// upgrade transaction. New rows arrive carrying the permanent url, which does
// not expire, so this is the last time it has to happen.
//
// v4 (CYBORG-987): adds the `pages` store. Agent DMs reuse the `messages` store
// (their rows ARE ledger `Message`s), but an agent TIMELINE and a session's WORK are
// `AgentTimelineItem` records from `agent_timeline_rows` — a different shape this
// store cannot hold. Purely ADDITIVE: nothing is dropped on this hop, so the 3→4
// upgrade keeps every cached message.
//
// v5: adds a separate owner-scoped agent prompt outbox. Unlike channel/DM
// sends, an agent RPC acknowledgement does not prove the daemon persisted the
// prompt, so these rows remain until the correlated timeline item arrives.
//
// v6: cached messages are dropped once more (same reasoning as v3): their reaction chips
// carry a baked "I reacted" flag and display names but no reactor ids, which the id-keyed
// reaction model cannot reconcile. New rows store `reactors`.
export const DB_VERSION = 6;

/**
 * Stored message shape. `createdAt`/`updatedAt` are epoch-ms numbers to match
 * the rewrite's `Message` type (v1 stored ISO strings). The composite index is
 * keyed on the zero-padded createdAt STRING (`createdAtKey`) so a lexicographic
 * cursor scan == chronological order regardless of number width.
 *
 * `cachedAt` is local-clock epoch ms used purely for LRU/orphan eviction.
 */
export interface CachedMessage {
  id: string;
  scopeKey: string;
  workspaceId: string | null;
  channelId: string | null;
  fromId: string;
  fromType: "human" | "agent" | "system";
  fromName: string | null;
  toId: string | null;
  text: string;
  mentions: string[] | null;
  parentId: string | null;
  attachments: unknown[] | null;
  unfurls: unknown[] | null;
  // Structured rich card (an integration offer, a webhook release card). Persisted
  // because the surface this cache newly serves is the agent DM, which is where cards
  // actually arrive: without it a cold paint renders the card's plain-text FALLBACK and
  // the real card appears only once the network answer lands — a visible flip on the
  // very first offer you receive. Rows written before v4 read back `undefined` → null,
  // so no migration is needed. Typed `unknown` (never `MessageCard`) to keep this
  // module free of core-type imports, exactly like `attachments`/`unfurls`; null when
  // the message carries no card.
  card: unknown;
  // The turn-end summary of an agent post (steps, outcome, missing permissions); absent on older rows.
  workSummary?: unknown;
  reactions: CachedReaction[];
  pinnedAt: number | null;
  pinnedBy: string | null;
  replyCount: number;
  lastReplyAt: number | null;
  threadParticipants:
    | { id?: string; kind?: "human" | "agent"; name: string; image?: string | null }[]
    | null;
  // The session's cybo on an agent reply (fetch_thread); absent on older rows.
  cyboId?: string;
  // Origin marker / system-line state (an `agent_hop_limit` line offers Continue; continued or spent, it does not).
  source?: string;
  seq: number;
  createdAt: number;
  updatedAt: number | null;
  /** Zero-padded createdAt for lexicographic index ordering. */
  createdAtKey: string;
  cachedAt: number;
  deleted: boolean;
}

export type CachedReaction = import("../core/types.js").Reaction;

export interface SyncMeta {
  scopeKey: string;
  /** Highest message `seq` we've cached for this scope (delta-sync cursor). */
  lastSyncedSeq: number;
  /** The user's read cursor (epoch ms) so a cold revisit can render the
   *  unread divider without waiting on the network. */
  lastReadAt: number | null;
  /** Epoch ms. Used for LRU eviction when total scope count is too high. */
  lastAccessedAt: number;
  /** Epoch ms. When we first cached this scope (telemetry / debugging). */
  firstCachedAt: number;
}

interface AppMeta {
  key: string;
  value: string;
}

/**
 * A queued send that couldn't be delivered (socket down or open-but-
 * unauthenticated). Persisted so an offline send survives a reload (#502) — the
 * in-memory replay queue is rebuilt from these on startup, and each row is
 * deleted once its broadcast echo reconciles the optimistic bubble.
 *
 * Keyed by `clientMsgId` (#501): the same id is stamped on the optimistic
 * bubble and echoed by the relay, so a delivered send is dequeued exactly and a
 * replay never duplicates. Everything stored is JSON-serializable — no closures.
 */
export interface OutboxRecord {
  /** Client-generated id; also the optimistic row's id (`local-<clientMsgId>`). */
  clientMsgId: string;
  /** Owning user — lets a shared-WebView wipe drop another user's queue. */
  userId: string;
  kind: "channel" | "dm";
  workspaceId: string;
  /** Channel id (channel send) or peer user id (DM send). */
  targetId: string;
  text: string;
  mentions: string[] | null;
  /** Ids picked from the @-autocomplete. Absent on records queued before it existed. */
  mentionPicks?: string[] | null;
  attachments: unknown[] | null;
  /** Thread root id when this is a threaded send; null for top-level. */
  parentId: string | null;
  /** Epoch ms the send was first attempted — preserves chronological replay. */
  createdAt: number;
  /** The bubble's own time (see bubbleTime); absent on records written before it existed. */
  bubbleAt?: number;
}

export interface AgentPromptOutboxRecord {
  clientMessageId: string;
  owner: string;
  workspaceId: string;
  agentId: string;
  /**
   * The delivery attempt the RELAY minted for this send and returned on the ack.
   * With it a reload can ask the owning daemon what became of the prompt instead of
   * guessing; absent for a row written before the ack, or by an older relay.
   */
  promptAttemptId?: string;
  text: string;
  attachments: unknown[] | null;
  pageCard: unknown | null;
  workSessionId: string | null;
  createdAt: number;
  /** The daemon held it behind a running turn: a reload restores it into the queue bar only. */
  queued?: true;
}

/**
 * One durable SERVER PAGE, cached whole and keyed by the conversation it belongs to
 * (key builders in ./pages.ts). The `messages` store cannot hold these: an agent
 * timeline and a session's work are `AgentTimelineItem` records, not `Message`s, and
 * the cybo-DM ledger window is the merge's own candidate shape.
 *
 * ONE ROW PER KEY, not one row per item. Every consumer replays a WHOLE page in one
 * call (`hydrateFromTimeline`, the ledger merge), so a single get/put is the entire
 * access pattern — no per-item rows, no ordering index, no cursor scan. Each page is
 * bounded by the server (`limit` defaults to 100, ws-client.ts) and the NUMBER of rows
 * is bounded by the LRU sweep in ./pages.ts.
 */
export interface CachedPage {
  key: string;
  items: unknown[];
  /** Backward-paging state, verbatim from the page this row was written from. */
  hasOlder: boolean;
  olderCursor: string | null;
  /** Committed high-water seq of the cached snapshot (null when the daemon sent none). */
  maxSeq: number | null;
  /** The timeline's durable life the rows belong to; absent from older rows and daemons. */
  life?: string;
  /** Local-clock epoch ms — the LRU key. */
  cachedAt: number;
}

const STORE_MESSAGES = "messages";
const STORE_SYNC_META = "syncMeta";
const STORE_APP_META = "appMeta";
const STORE_OUTBOX = "outbox";
const STORE_PAGES = "pages";
const STORE_AGENT_PROMPT_OUTBOX = "agentPromptOutbox";

const IDX_SCOPE_CREATED = "by-scope-created";
const IDX_PARENT = "by-parent";
const IDX_ACCESSED = "by-accessed";
const IDX_CACHED = "by-cached";

/** 20-char zero-padded numeric string — fits any JS-safe epoch-ms integer. */
export function createdAtKey(createdAt: number): string {
  const n = Number.isFinite(createdAt) ? Math.max(0, Math.floor(createdAt)) : 0;
  return String(n).padStart(20, "0");
}

let dbPromise: Promise<IDBDatabase> | null = null;

/** True when IDB is usable in the current runtime. */
export function isAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

/**
 * Lazy DB open. Multiple callers share one connection. Schema upgrades go in
 * `onupgradeneeded` — bump `DB_VERSION` and add a branch for future changes.
 */
export function getDB(): Promise<IDBDatabase> | null {
  if (!isAvailable()) return null;
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      let req: IDBOpenDBRequest;
      try {
        req = indexedDB.open(DB_NAME, DB_VERSION);
      } catch (err) {
        reject(err);
        return;
      }
      req.onupgradeneeded = (evt) => {
        const db = req.result;
        // v3 (CYBORG-897 phase 4): drop the cached messages ONCE, because every
        // pre-cutover row carries an expiring signed attachment url that a cold
        // paint would replay. v6 drops them once more (legacy reaction chips, see
        // DB_VERSION). Guarded on the OLD version — a fresh install (oldVersion 0) has nothing
        // to drop and takes the create path below untouched. `syncMeta` goes with
        // it: leaving a per-scope `lastSyncedSeq` behind for messages that no
        // longer exist would make the delta sync skip precisely the rows just
        // deleted, i.e. a permanently empty scope.
        if (evt.oldVersion > 0 && evt.oldVersion < 6) {
          if (db.objectStoreNames.contains(STORE_MESSAGES)) db.deleteObjectStore(STORE_MESSAGES);
          if (db.objectStoreNames.contains(STORE_SYNC_META)) db.deleteObjectStore(STORE_SYNC_META);
        }
        if (!db.objectStoreNames.contains(STORE_MESSAGES)) {
          const messages = db.createObjectStore(STORE_MESSAGES, { keyPath: "id" });
          messages.createIndex(IDX_SCOPE_CREATED, ["scopeKey", "createdAtKey"]);
          messages.createIndex(IDX_PARENT, "parentId");
        }
        if (!db.objectStoreNames.contains(STORE_SYNC_META)) {
          const syncMeta = db.createObjectStore(STORE_SYNC_META, { keyPath: "scopeKey" });
          syncMeta.createIndex(IDX_ACCESSED, "lastAccessedAt");
        }
        if (!db.objectStoreNames.contains(STORE_APP_META)) {
          db.createObjectStore(STORE_APP_META, { keyPath: "key" });
        }
        // v2 (#502): persistent send outbox, keyed by clientMsgId.
        if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
          db.createObjectStore(STORE_OUTBOX, { keyPath: "clientMsgId" });
        }
        // v4 (CYBORG-987): cached server pages (agent timeline / session work / the
        // cybo-DM ledger window), one row per key, LRU-evicted on `cachedAt`.
        if (!db.objectStoreNames.contains(STORE_PAGES)) {
          const pages = db.createObjectStore(STORE_PAGES, { keyPath: "key" });
          pages.createIndex(IDX_CACHED, "cachedAt");
        }
        if (!db.objectStoreNames.contains(STORE_AGENT_PROMPT_OUTBOX)) {
          db.createObjectStore(STORE_AGENT_PROMPT_OUTBOX, { keyPath: "clientMessageId" });
        }
      };
      req.onsuccess = () => {
        const db = req.result;
        // Another tab/instance wants to upgrade → close so it can proceed.
        db.onversionchange = () => {
          try {
            db.close();
          } catch {
            /* */
          }
          dbPromise = null;
        };
        resolve(db);
      };
      req.onerror = () => reject(req.error);
      req.onblocked = () => {
        // An older connection is holding the DB; the open will resolve once it
        // closes. Nothing to surface — transient.
      };
    });
    // If the open ever rejects, drop the cached promise so the next call retries.
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

/** Promisify an IDBRequest. */
export function reqDone<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Promisify a transaction completing. */
export function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export const STORES = {
  MESSAGES: STORE_MESSAGES,
  SYNC_META: STORE_SYNC_META,
  APP_META: STORE_APP_META,
  OUTBOX: STORE_OUTBOX,
  PAGES: STORE_PAGES,
  AGENT_PROMPT_OUTBOX: STORE_AGENT_PROMPT_OUTBOX,
} as const;

export const INDEXES = {
  SCOPE_CREATED: IDX_SCOPE_CREATED,
  PARENT: IDX_PARENT,
  ACCESSED: IDX_ACCESSED,
  CACHED: IDX_CACHED,
} as const;

export type { AppMeta };

/** Force-close the connection so the next caller re-opens. */
export async function closeDB(): Promise<void> {
  if (!dbPromise) return;
  try {
    const db = await dbPromise;
    db.close();
  } catch {
    /* */
  }
  dbPromise = null;
}
